export const BILLING_MODELS = Object.freeze({
  CHARGE: 'charge',
  SUBSCRIPTION: 'subscription',
  MIXED: 'mixed',
});

/**
 * Derive a product's billing composition from its ENABLED prices:
 *   only one-time  -> 'charge'
 *   only recurring -> 'subscription'
 *   both           -> 'mixed'
 *
 * Falls back to the legacy `product.type` when there are no prices. Mirrors the
 * API's computeBillingModel (apps/api/src/products/lib/computeBillingModel.ts) —
 * keep the two in sync. Computed live here (every product already carries
 * prices[]) so the pages-list columns are correct without a migration.
 */
export default function getBillingModel(product) {
  const enabled = (product?.prices ?? []).filter(price => price?.enabled !== false);

  if (enabled.length > 0) {
    const hasOneTime = enabled.some(price => price.billingType === 'one_time');
    const hasRecurring = enabled.some(price => price.billingType === 'recurring');

    if (hasOneTime && hasRecurring) return BILLING_MODELS.MIXED;
    if (hasRecurring) return BILLING_MODELS.SUBSCRIPTION;
    return BILLING_MODELS.CHARGE;
  }

  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run
  return product?.type === 'subscription' ? BILLING_MODELS.SUBSCRIPTION : BILLING_MODELS.CHARGE;
}

/**
 * Whether a product can produce a recurring purchase (subscription | mixed) — the
 * multi-price-aware replacement for `product.type === 'subscription'` UI gates.
 */
export function hasRecurringPrice(product) {
  const model = getBillingModel(product);
  return model === BILLING_MODELS.SUBSCRIPTION || model === BILLING_MODELS.MIXED;
}

/**
 * Whether a product can produce a one-time purchase (charge | mixed) — the
 * multi-price-aware replacement for `product.type !== 'subscription'` UI gates.
 */
export function hasOneTimePrice(product) {
  const model = getBillingModel(product);
  return model === BILLING_MODELS.CHARGE || model === BILLING_MODELS.MIXED;
}

/**
 * Whether the product sells through an existing Stripe-managed price — the
 * multi-price-aware replacement for `product.planManagedInStripe` UI gates.
 */
export function hasStripeManagedPrice(product) {
  const enabled = (product?.prices ?? []).filter(price => price?.enabled !== false);
  return enabled.some(price => price?.managedInStripe === true);
}

/**
 * Page-level recurring gate for components that only have the page in scope
 * (no product.prices). Prefers the denormalized page.billingModel; pages
 * created before the multi-price backfill lack it, so fall back to the legacy
 * binary productType.
 */
// TODO(M14): collapse to page.billingModel once the backfill migration has run
export function pageHasRecurringBilling(page) {
  return page?.billingModel
    ? page.billingModel !== BILLING_MODELS.CHARGE
    : page?.productType === 'subscription';
}
